import { z } from 'zod';
/**
 * Onboarding wizard · Step 5 (governance facts).
 *
 * The "last bit" of the redesigned wizard, captured in the `GovOnboardingStep`
 * component at
 * `tmp/onboarding-mock/bundle/leanopx/project/assets/05f37e62-…js`
 * lines 524–626. The mock surfaces:
 *   - an auto-fill button that infers entity type, state of inc., fiscal
 *     year, EIN-prefix, last round, board composition, and employee
 *     states from Carta + Drive,
 *   - an inferred panel showing the seven derived rows,
 *   - a stage chip-pick (`stageDetailed`) with the 7-option set on
 *     line 594, and
 *   - a "what are you least confident about?" multi-select with the
 *     7-option set on line 605.
 *
 * Existing shared types deliberately distinct from these:
 *   - `OrgPinnedEntityType`  (org-pinned-entity.ts)  — pin-graph entity
 *                                                       kinds, unrelated.
 *   - `GovernanceItem` / `GovernanceCategory` / `stageGate`
 *                            (governance.ts)         — the year-ahead
 *                                                       timeline that
 *                                                       the wizard's
 *                                                       answers
 *                                                       parameterize.
 *                                                       The wizard's
 *                                                       picks feed
 *                                                       generation of
 *                                                       those items;
 *                                                       they aren't the
 *                                                       items themselves.
 *   - `CompanyStage`        (company-enrichment.ts)  — public
 *                                                       enrichment
 *                                                       stage set
 *                                                       (`Series C+`
 *                                                       collapses
 *                                                       Series C/D/Growth);
 *                                                       the wizard
 *                                                       distinguishes
 *                                                       `series-c` /
 *                                                       `series-d-plus` /
 *                                                       `growth` to drive
 *                                                       the
 *                                                       stage-specific
 *                                                       banner.
 */
/** Closed entity-type set from the mock's auto-fill ("Delaware C-Corp")
 *  plus the realistic siblings the gov timeline needs to distinguish. */
export const OnboardingEntityType = z.enum([
    'c-corp-de',
    'c-corp-other',
    's-corp',
    'llc',
    'lp',
    'pbc',
    'other',
]);
export const ONBOARDING_ENTITY_TYPE_LABEL = {
    'c-corp-de': 'Delaware C-Corp',
    'c-corp-other': 'C-Corp (other state)',
    's-corp': 'S-Corp',
    llc: 'LLC',
    lp: 'LP',
    pbc: 'Public Benefit Corp',
    other: 'Other',
};
/**
 * U.S. state postal codes + DC. The mock's auto-fill picks "Delaware"
 * for `incState` and "CA, NY, WA, MA" for `empStates` — we store both
 * fields as state-code slugs so the gov-timeline generator can match
 * on `WA` rather than parsing free text. Includes DC since C-Corp inc.
 * in DC is a real (if uncommon) choice.
 */
export const OnboardingIncState = z.enum([
    'AL',
    'AK',
    'AZ',
    'AR',
    'CA',
    'CO',
    'CT',
    'DE',
    'DC',
    'FL',
    'GA',
    'HI',
    'ID',
    'IL',
    'IN',
    'IA',
    'KS',
    'KY',
    'LA',
    'ME',
    'MD',
    'MA',
    'MI',
    'MN',
    'MS',
    'MO',
    'MT',
    'NE',
    'NV',
    'NH',
    'NJ',
    'NM',
    'NY',
    'NC',
    'ND',
    'OH',
    'OK',
    'OR',
    'PA',
    'RI',
    'SC',
    'SD',
    'TN',
    'TX',
    'UT',
    'VT',
    'VA',
    'WA',
    'WV',
    'WI',
    'WY',
]);
/** Funding-round chip set from the mock's `stageDetailed` picker
 *  (line 594). 7-option enum; `growth` covers "Growth / pre-IPO". */
export const OnboardingFundingRound = z.enum([
    'pre-seed',
    'seed',
    'series-a',
    'series-b',
    'series-c',
    'series-d-plus',
    'growth',
]);
export const ONBOARDING_FUNDING_ROUND_LABEL = {
    'pre-seed': 'Pre-seed',
    seed: 'Seed',
    'series-a': 'Series A',
    'series-b': 'Series B',
    'series-c': 'Series C',
    'series-d-plus': 'Series D+',
    growth: 'Growth / pre-IPO',
};
/** "What are you least confident about?" multi-select chip set (line 605). */
export const OnboardingGovernanceWorry = z.enum([
    'delaware-filings',
    'option-grants-409a',
    'board-cadence',
    'multi-state-payroll',
    'soc2-privacy',
    'audit-ipo-readiness',
    'done-it-before',
]);
export const ONBOARDING_GOVERNANCE_WORRY_LABEL = {
    'delaware-filings': 'Delaware filings',
    'option-grants-409a': 'Option grants & 409A',
    'board-cadence': 'Board cadence',
    'multi-state-payroll': 'Multi-state payroll',
    'soc2-privacy': 'SOC 2 / privacy',
    'audit-ipo-readiness': 'Audit & IPO readiness',
    'done-it-before': "I've done it all before",
};
/**
 * EIN format: 9 digits with a hyphen after the first 2 (e.g. "87-3221987").
 * The mock displays a redacted prefix ("87-3****") because the user hasn't
 * typed the rest yet; on the wire we accept either the full 9-digit form or
 * a prefix-and-stars form so the partial-fill state round-trips.
 */
const EIN_REGEX = /^\d{2}-\d{7}$|^\d{2}-\d{1,4}\*{3,}$/;
/**
 * Persisted record for the wizard's gov-step answers. `orgId` is the
 * first field for multi-tenant scoping; everything else mirrors the
 * `d` state shape the mock's `GovOnboardingStep` writes into.
 */
export const OnboardingGovernancePreferences = z.object({
    orgId: z.string().min(1),
    entityType: OnboardingEntityType,
    incState: OnboardingIncState,
    /** Fiscal-year-start month (1-12). Calendar year (Dec 31 close) → 1. */
    fiscalYearStartMonth: z.number().int().min(1).max(12),
    /** EIN — full 9-digit or partial-mask form ("87-3****"). Optional
     *  because the wizard often runs before the EIN is on hand. */
    ein: z.string().regex(EIN_REGEX, 'expected NN-NNNNNNN or partial mask').optional(),
    lastRound: OnboardingFundingRound,
    /** Board seat count from the mock ("5 (2 founders, 2 investors, 1 independent)"). */
    boardSize: z.number().int().min(0).max(20),
    /** States where the company has employees, as USPS codes. Drives the
     *  multi-state payroll items in the gov timeline. */
    empStates: z.array(OnboardingIncState).max(56),
    /** True iff the panel was populated by the auto-fill ("Inferred · review
     *  and edit"); cleared when the operator hand-edits any row. */
    govInferred: z.boolean(),
    /** Multi-select chip values from the "least confident about" picker. */
    govWorries: z.array(OnboardingGovernanceWorry).max(7),
    /** Free-text textarea — anything the operator wants the agent to know
     *  about gov state beyond the closed-enum picks. */
    specialAttention: z.string().max(2000).optional(),
    /** Detailed stage chip-pick — drives the "stage-specific banner"
     *  rendered on the timeline. */
    stageDetailed: OnboardingFundingRound,
    /** ISO-8601 timestamp of the last server-side write. */
    updatedAt: z.string().datetime(),
    /** Firebase uid of the writer. */
    updatedBy: z.string().min(1),
});
/**
 * Body of `PUT /api/orgs/:o/onboarding/governance`. The
 * handler derives `orgId` + `updatedAt` + `updatedBy` from the request
 * context. The client never sends those three; the server-side write
 * stamps them on commit.
 */
export const UpdateOnboardingGovernancePreferencesRequest = OnboardingGovernancePreferences.omit({
    orgId: true,
    updatedAt: true,
    updatedBy: true,
});
